import { useEffect, useState } from 'react';

export enum AnimatedMountStatus {
  Open = 'open',
  Closing = 'closing',
}

type UseAnimatedMountResult = {
  isMounted: boolean;
  status: AnimatedMountStatus;
};

// Matches the default $transition-time used across the app's SCSS animations.
const DEFAULT_ANIMATION_DURATION_MS = 350;

/**
 * Keeps an element mounted through its exit animation, then unmounts it from the DOM.
 *
 * Designed to pair with CSS `@keyframes` (not transitions): the element mounts already
 * carrying the "open" class, so its enter animation plays immediately.
 * On close it swaps to the "closing" status and unmounts once `durationMs` has elapsed.
 *
 * @param isOpen      Desired open state (the source of truth, e.g. a dropdown's open flag).
 * @param durationMs  Exit animation duration in ms (defaults to 350ms); keep in sync with the CSS animation time.
 */
function useAnimatedMount(
  isOpen: boolean,
  durationMs: number = DEFAULT_ANIMATION_DURATION_MS,
): UseAnimatedMountResult {
  const [isMounted, setIsMounted] = useState<boolean>(isOpen);
  const [status, setStatus] = useState<AnimatedMountStatus>(
    isOpen ? AnimatedMountStatus.Open : AnimatedMountStatus.Closing,
  );

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setStatus(AnimatedMountStatus.Open);

      return undefined;
    }

    setStatus(AnimatedMountStatus.Closing);
    const timeoutId = setTimeout(() => setIsMounted(false), durationMs);

    return () => clearTimeout(timeoutId);
  }, [isOpen, durationMs]);

  return { isMounted, status };
}

export default useAnimatedMount;
